import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, ArrowLeft, Share2, Eye, MapPin, Calendar, User, Star } from 'lucide-react';
import { toast } from 'sonner';
import {
  blogsAPI,
  resolveImageUrl,
  CATEGORY_LABELS,
  CATEGORY_COLORS,
} from '../services/blogsApi';

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://geostays.co';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch (_e) {
    return '';
  }
};

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState({ related_blogs: [], properties: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await blogsAPI.getBySlug(slug);
        if (!cancelled && res.success) setBlog(res.data);
        // Fetch related in parallel — silent on failure
        blogsAPI.related(slug).then((r) => {
          if (!cancelled && r.success) {
            setRelated({
              related_blogs: r.related_blogs || [],
              properties: r.properties || [],
            });
          }
        }).catch(() => {});
      } catch (_e) {
        if (!cancelled) {
          toast.error('Blog not found');
          navigate('/blog', { replace: true });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, navigate]);

  // Update <title>, meta description, canonical + JSON-LD schema for SEO on client side.
  useEffect(() => {
    if (!blog) return;
    const originalTitle = document.title;
    document.title = `${blog.title} — GeoStays Blog`;

    const setMeta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content || '');
      return el;
    };

    const descEl = setMeta('description', blog.excerpt || '');
    const ogTitleEl = setMeta('og:title', blog.title, 'property');
    const ogDescEl = setMeta('og:description', blog.excerpt || '', 'property');
    const ogImageEl = setMeta('og:image', resolveImageUrl(blog.cover_image) || '', 'property');
    const ogTypeEl = setMeta('og:type', 'article', 'property');

    // JSON-LD Article schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'blog-jsonld';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: blog.title,
      description: blog.excerpt,
      image: resolveImageUrl(blog.cover_image),
      datePublished: blog.published_at,
      dateModified: blog.updated_at,
      author: {
        '@type': 'Person',
        name: blog.author?.name || 'GeoStays',
      },
      publisher: {
        '@type': 'Organization',
        name: 'GeoStays',
        logo: {
          '@type': 'ImageObject',
          url: 'https://customer-assets.emergentagent.com/job_stayswap-3/artifacts/greklbdc_GEOSTAYS-01.jpg',
        },
      },
      mainEntityOfPage: `${SITE_URL}/blog/${blog.slug}`,
      keywords: (blog.tags || []).join(', '),
    });
    document.head.appendChild(script);

    return () => {
      document.title = originalTitle;
      // Best-effort cleanup so meta doesn't leak across pages
      [descEl, ogTitleEl, ogDescEl, ogImageEl, ogTypeEl].forEach((el) => {
        if (el && el.parentNode) el.parentNode.removeChild(el);
      });
      const s = document.getElementById('blog-jsonld');
      if (s && s.parentNode) s.parentNode.removeChild(s);
    };
  }, [blog]);

  const handleShare = async () => {
    const url = `${window.location.origin}/blog/${blog.slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: blog.title, text: blog.excerpt, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard');
      }
    } catch (_e) {
      // user cancelled
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-[#7CB342]" />
      </div>
    );
  }

  if (!blog) return null;

  const cat = blog.category;

  return (
    <div className="min-h-screen bg-white">
      {/* Back nav */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl pt-6">
        <Button variant="ghost" onClick={() => navigate('/blog')} className="text-gray-600">
          <ArrowLeft className="h-4 w-4 mr-2" /> All Stories
        </Button>
      </div>

      {/* Header */}
      <article className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl py-6">
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {cat && (
            <span
              className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                CATEGORY_COLORS[cat] || 'bg-gray-100 text-gray-700'
              }`}
              data-testid="blog-category"
            >
              {CATEGORY_LABELS[cat] || cat}
            </span>
          )}
          {blog.destination && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
              <MapPin className="h-3 w-3" /> {blog.destination}
            </span>
          )}
        </div>

        <h1
          className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 leading-tight"
          data-testid="blog-title"
        >
          {blog.title}
        </h1>
        <p className="text-lg text-gray-600 mt-4 leading-relaxed">{blog.excerpt}</p>

        {/* Meta bar */}
        <div className="flex items-center justify-between mt-6 pb-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#7CB342] text-white flex items-center justify-center text-sm font-semibold">
              {(blog.author?.name || 'G').charAt(0).toUpperCase()}
            </div>
            <div className="text-sm">
              <div className="font-medium text-gray-900 flex items-center gap-1">
                {blog.author?.name || 'GeoStays'}
                {blog.author?.role === 'admin' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                    Team
                  </span>
                )}
                {blog.author?.role === 'host' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                    Host
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> {formatDate(blog.published_at || blog.created_at)}
                </span>
                <span className="flex items-center gap-1">
                  <Eye className="h-3 w-3" /> {blog.views || 0} views
                </span>
              </div>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleShare}
            data-testid="blog-share-btn"
          >
            <Share2 className="h-4 w-4 mr-2" /> Share
          </Button>
        </div>

        {/* Cover */}
        {blog.cover_image && (
          <div className="mt-8 rounded-xl overflow-hidden bg-gray-100 aspect-[16/9]">
            <img
              src={resolveImageUrl(blog.cover_image)}
              alt={blog.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Body */}
        <div
          className="blog-body prose prose-lg max-w-none mt-8"
          dangerouslySetInnerHTML={{ __html: blog.body_html }}
          data-testid="blog-body"
        />

        {/* Tags */}
        {blog.tags && blog.tags.length > 0 && (
          <div className="mt-10 pt-6 border-t flex flex-wrap gap-2">
            {blog.tags.map((t) => (
              <Link
                key={t}
                to={`/blog?search=${encodeURIComponent(t)}`}
                className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                #{t}
              </Link>
            ))}
          </div>
        )}

        {/* Footer share nudge */}
        <div className="mt-10 py-6 border-t border-b flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-600">
            <User className="h-4 w-4 inline mr-1" /> Enjoyed the story? Share it with fellow travellers.
          </div>
          <Button onClick={handleShare} className="bg-[#7CB342] hover:bg-[#6da036]">
            <Share2 className="h-4 w-4 mr-2" /> Share this story
          </Button>
        </div>
      </article>

      {/* Related content — keeps readers exploring */}
      {(related.related_blogs.length > 0 || related.properties.length > 0) && (
        <section className="bg-gray-50 border-t mt-10 py-12" data-testid="related-section">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl space-y-12">
            {related.related_blogs.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">You might also like</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="related-blogs">
                  {related.related_blogs.map((b) => (
                    <Link
                      key={b.id}
                      to={`/blog/${b.slug || b.id}`}
                      className="group block"
                      data-testid={`related-blog-${b.id}`}
                    >
                      <Card className="overflow-hidden h-full transition-all group-hover:shadow-lg group-hover:-translate-y-0.5">
                        {b.cover_image && (
                          <div className="aspect-[16/10] overflow-hidden bg-gray-100">
                            <img
                              src={resolveImageUrl(b.cover_image)}
                              alt={b.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        )}
                        <CardContent className="p-4">
                          <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full mb-2 ${
                            CATEGORY_COLORS[b.category] || 'bg-gray-100 text-gray-700'
                          }`}>
                            {CATEGORY_LABELS[b.category] || b.category}
                          </span>
                          <h3 className="font-semibold text-sm leading-snug line-clamp-2 group-hover:text-[#7CB342] transition-colors">
                            {b.title}
                          </h3>
                          <p className="text-xs text-gray-500 mt-2 line-clamp-2">{b.excerpt}</p>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {related.properties.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Book a stay in {blog.destination || 'India'}</h2>
                <p className="text-gray-600 mb-6 text-sm">
                  Hand-picked homestays that match this story&apos;s vibe.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="related-properties">
                  {related.properties.map((p) => (
                    <Link
                      key={p.id}
                      to={`/property/${p.slug || p.id}`}
                      className="group block"
                      data-testid={`related-property-${p.id}`}
                    >
                      <Card className="overflow-hidden h-full transition-all group-hover:shadow-lg group-hover:-translate-y-0.5">
                        {p.images?.[0] && (
                          <div className="aspect-[16/10] overflow-hidden bg-gray-100">
                            <img
                              src={resolveImageUrl(p.images[0])}
                              alt={p.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        )}
                        <CardContent className="p-4">
                          <h3 className="font-semibold text-sm leading-snug line-clamp-1 group-hover:text-[#7CB342] transition-colors">
                            {p.title}
                          </h3>
                          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                            <MapPin className="h-3 w-3" /> {p.location}
                          </p>
                          <div className="flex items-center justify-between mt-3 text-sm">
                            <span className="font-semibold">₹{p.price?.toLocaleString?.() ?? p.price}
                              <span className="text-xs text-gray-500 font-normal"> / night</span>
                            </span>
                            {p.rating > 0 && (
                              <span className="flex items-center gap-1 text-xs text-gray-600">
                                <Star className="h-3 w-3 fill-current text-yellow-500" /> {p.rating}
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogDetailPage;
